import { motion } from 'framer-motion';
import { Loader2, Plane, Users, FileText, Globe } from 'lucide-react';

// Professional Loading Spinner with Pulse Effect
const LoadingSpinner = ({ size = 'md', color = 'cyan' }) => {
  const sizes = {
    sm: 'h-6 w-6',
    md: 'h-10 w-10',
    lg: 'h-16 w-16'
  };
  
  const colors = {
    cyan: 'text-cyan-500',
    orange: 'text-orange-500',
    emerald: 'text-emerald-500',
    blue: 'text-blue-500'
  };
  
  return (
    <div className="relative">
      <div className={`absolute inset-0 ${colors[color]} blur-xl opacity-30 animate-pulse`} />
      <Loader2 className={`${sizes[size]} ${colors[color]} animate-spin`} />
    </div>
  );
};

// Animated Skeleton with Shimmer Effect
const AnimatedSkeleton = ({ className = '', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: delay * 0.1 }}
    className={`relative overflow-hidden bg-slate-800/50 rounded-2xl ${className}`}
  >
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/5 to-transparent" />
  </motion.div>
);

// Dashboard Loading State
export const DashboardLoading = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div>
        <AnimatedSkeleton className="h-10 w-48 mb-2" delay={0} />
        <AnimatedSkeleton className="h-4 w-64" delay={1} />
      </div>
      <AnimatedSkeleton className="h-48 w-48 rounded-full" delay={2} />
    </div>
    
    {/* Stats Cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <AnimatedSkeleton key={i} className="h-32" delay={i + 3} />
      ))}
    </div>
    
    {/* Charts */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <AnimatedSkeleton className="h-48 lg:col-span-2" delay={7} />
      <AnimatedSkeleton className="h-48" delay={8} />
    </div>
    
    {/* Secondary Stats */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <AnimatedSkeleton key={i} className="h-28" delay={i + 9} />
      ))}
    </div>
    
    {/* Center Loading Indicator */}
    <div className="flex flex-col items-center justify-center py-8">
      <LoadingSpinner size="lg" color="cyan" />
      <p className="text-slate-500 text-sm mt-4 animate-pulse">Loading dashboard data...</p>
    </div>
  </div>
);

// Suppliers Page Loading State
export const SuppliersLoading = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div>
        <AnimatedSkeleton className="h-10 w-36 mb-2" delay={0} />
        <AnimatedSkeleton className="h-4 w-48" delay={1} />
      </div>
      <AnimatedSkeleton className="h-12 w-40 rounded-xl" delay={2} />
    </div>
    
    {/* Search */}
    <AnimatedSkeleton className="h-12 w-full rounded-xl" delay={3} />
    
    {/* Stats */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <AnimatedSkeleton key={i} className="h-24" delay={i + 4} />
      ))}
    </div>
    
    {/* Supplier Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
      {[...Array(8)].map((_, i) => (
        <AnimatedSkeleton key={i} className="h-72" delay={i + 8} />
      ))}
    </div>
    
    {/* Center Loading */}
    <div className="flex flex-col items-center justify-center py-4">
      <div className="relative">
        <Users className="h-12 w-12 text-cyan-500/30" />
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner size="sm" color="cyan" />
        </div>
      </div>
      <p className="text-slate-500 text-sm mt-3 animate-pulse">Loading suppliers...</p>
    </div>
  </div>
);

// Flight Tracker Loading State
export const FlightTrackerLoading = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <AnimatedSkeleton className="h-11 w-11 rounded-full" delay={0} />
        <div>
          <AnimatedSkeleton className="h-10 w-48 mb-2" delay={1} />
          <AnimatedSkeleton className="h-4 w-56" delay={2} />
        </div>
      </div>
      <AnimatedSkeleton className="h-12 w-36 rounded-xl" delay={3} />
    </div>
    
    {/* Stats */}
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
      {[...Array(5)].map((_, i) => (
        <AnimatedSkeleton key={i} className="h-24" delay={i + 4} />
      ))}
    </div>
    
    {/* Search */}
    <AnimatedSkeleton className="h-12 w-full rounded-xl" delay={9} />
    
    {/* Ticket Cards */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {[...Array(4)].map((_, i) => (
        <AnimatedSkeleton key={i} className="h-80 rounded-3xl" delay={i + 10} />
      ))}
    </div>
    
    {/* Center Loading */}
    <div className="flex flex-col items-center justify-center py-4">
      <div className="relative">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        >
          <Plane className="h-12 w-12 text-orange-500/50" />
        </motion.div>
      </div>
      <p className="text-slate-500 text-sm mt-3 animate-pulse">Loading flight tickets...</p>
    </div>
  </div>
);

// Visa Center Loading State
export const VisaCenterLoading = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex items-center gap-4">
      <AnimatedSkeleton className="h-11 w-11 rounded-full" delay={0} />
      <div>
        <AnimatedSkeleton className="h-10 w-32 mb-2" delay={1} />
        <AnimatedSkeleton className="h-4 w-48" delay={2} />
      </div>
    </div>
    
    {/* Action Buttons */}
    <div className="flex gap-3">
      <AnimatedSkeleton className="flex-1 h-14 rounded-xl" delay={3} />
      <AnimatedSkeleton className="h-14 w-36 rounded-xl" delay={4} />
    </div>
    
    {/* Search */}
    <AnimatedSkeleton className="h-12 w-full rounded-xl" delay={5} />
    
    {/* Stats */}
    <div className="grid grid-cols-4 gap-3">
      {[...Array(4)].map((_, i) => (
        <AnimatedSkeleton key={i} className="h-20" delay={i + 6} />
      ))}
    </div>
    
    {/* Visa Cards */}
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <AnimatedSkeleton key={i} className="h-96" delay={i + 10} />
      ))}
    </div>
    
    {/* Center Loading */}
    <div className="flex flex-col items-center justify-center py-4">
      <div className="relative">
        <FileText className="h-12 w-12 text-emerald-500/30" />
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner size="sm" color="emerald" />
        </div>
      </div>
      <p className="text-slate-500 text-sm mt-3 animate-pulse">Loading visas...</p>
    </div>
  </div>
);

// Passport Tracker Loading State
export const PassportTrackerLoading = () => (
  <div className="space-y-6">
    {/* Header */}
    <div className="flex items-center gap-4">
      <AnimatedSkeleton className="h-11 w-11 rounded-full" delay={0} />
      <div>
        <AnimatedSkeleton className="h-10 w-48 mb-2" delay={1} />
        <AnimatedSkeleton className="h-4 w-56" delay={2} />
      </div>
    </div>
    
    {/* Stats */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <AnimatedSkeleton key={i} className="h-24" delay={i + 3} />
      ))}
    </div>
    
    {/* Tabs */}
    <AnimatedSkeleton className="h-12 w-64 rounded-xl" delay={7} />
    
    {/* Passport Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[...Array(6)].map((_, i) => (
        <AnimatedSkeleton key={i} className="h-48" delay={i + 8} />
      ))}
    </div>
    
    {/* Center Loading */}
    <div className="flex flex-col items-center justify-center py-4">
      <div className="relative">
        <Globe className="h-12 w-12 text-purple-500/30" />
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner size="sm" color="blue" />
        </div>
      </div>
      <p className="text-slate-500 text-sm mt-3 animate-pulse">Loading passports...</p>
    </div>
  </div>
);

// Generic Page Loading
export const PageLoading = ({ title = 'Loading...', icon: Icon = Loader2 }) => (
  <div className="min-h-[60vh] flex flex-col items-center justify-center">
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative"
    >
      {/* Background glow */}
      <div className="absolute inset-0 bg-cyan-500/20 rounded-full blur-3xl scale-150" />
      
      {/* Icon container */}
      <div className="relative bg-slate-900/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Icon className="h-16 w-16 text-cyan-400" />
        </motion.div>
      </div>
    </motion.div>
    
    <motion.p
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="text-slate-400 text-lg mt-6 font-medium"
    >
      {title}
    </motion.p>
    
    {/* Loading dots */}
    <div className="flex gap-1 mt-3">
      {[0, 1, 2].map((i) => (
        <motion.div
          key={i}
          className="w-2 h-2 rounded-full bg-cyan-500"
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
        />
      ))}
    </div>
  </div>
);

export default {
  DashboardLoading,
  SuppliersLoading,
  FlightTrackerLoading,
  VisaCenterLoading,
  PassportTrackerLoading,
  PageLoading,
  LoadingSpinner,
  AnimatedSkeleton
};
